import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import MenuItem from '@mui/material/MenuItem';
import { Delete, Add } from '@mui/icons-material';
import { generateObjectIdLike } from '../../util/objectId';
import { SCORE_COLOR_PALETTE, getScoreColor } from '../../util/scoreColors';

// Internal-only sentinel for the "No answer / not set" option in the value
// picker — MUI Select handles `null` awkwardly as a controlled value, so we
// translate to/from this string at the edges. On the wire, a rule with this
// row selected always carries `optionValue: null` — the category's "no
// answer" rule (the delta applied when a juror has no demographics entry at
// all for that category).
const NO_ANSWER_SENTINEL = '__NO_ANSWER__';

/**
 * Small swatch button that opens a palette + custom-hex picker for a
 * score's color. `color` is the score's explicit override (or undefined),
 * `autoColor` is what it'd render as without one — shown as the swatch
 * value so the button always reflects the color actually in use.
 */
function ScoreColorPicker({ color, autoColor, onChange, disabled }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const displayColor = color || autoColor;

  const choose = (nextColor) => {
    onChange(nextColor);
    setAnchorEl(null);
  };

  return (
    <React.Fragment>
      <Tooltip title="Score color">
        <span>
          <IconButton size="small" disabled={disabled} onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: displayColor,
                border: '1px solid rgba(0,0,0,0.25)',
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, width: 200 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Score color
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            {SCORE_COLOR_PALETTE.map((swatch) => (
              <Box
                key={swatch}
                onClick={() => choose(swatch)}
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: swatch,
                  cursor: 'pointer',
                  border: swatch === color ? '2px solid #000' : '1px solid rgba(0,0,0,0.25)',
                }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <input
              type="color"
              value={displayColor}
              onChange={(e) => choose(e.target.value)}
              style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
            />
            <Button size="small" onClick={() => choose(undefined)}>Use automatic</Button>
          </Stack>
        </Box>
      </Popover>
    </React.Fragment>
  );
}

/**
 * Editor for a set of score definitions with each score's scoring rules
 * nested directly beneath it — there is no score picker on a rule row
 * because a rule's `scoreDefinitionId` is fixed to its parent score by
 * construction, so there's nothing left for the user to choose.
 *
 * Shared between the case edit form and the template edit form; the caller
 * normalizes its own category shape (case demographicCategoryConfigs vs.
 * template categoryTemplates) into `categories: {name, options}[]`.
 */
export default function ScoreDefinitionsEditor({ categories, scoreDefinitions, scoringRules, onChange, disabled }) {
  const safeScores = scoreDefinitions || [];
  const safeRules = scoringRules || [];
  const safeCategories = categories || [];

  const addScore = () => {
    onChange(
      [...safeScores, { _id: generateObjectIdLike(), name: '', description: '', showOnJurorCard: true }],
      safeRules,
    );
  };

  const updateScore = (scoreId, updates) => {
    onChange(safeScores.map((s) => (s._id === scoreId ? { ...s, ...updates } : s)), safeRules);
  };

  const removeScore = (scoreId) => {
    onChange(
      safeScores.filter((s) => s._id !== scoreId),
      safeRules.filter((r) => r.scoreDefinitionId !== scoreId),
    );
  };

  const addRule = (scoreId) => {
    onChange(safeScores, [
      ...safeRules,
      {
        _id: generateObjectIdLike(),
        categoryName: safeCategories[0]?.name || '',
        // null ("No answer / not set") rather than '' — '' isn't one of the
        // Value select's options, so a new row would render as a confusing
        // blank instead of a real, visible selection.
        optionValue: null,
        scoreDefinitionId: scoreId,
        delta: 0,
      },
    ]);
  };

  const updateRule = (ruleId, updates) => {
    onChange(safeScores, safeRules.map((r) => (r._id === ruleId ? { ...r, ...updates } : r)));
  };

  const removeRule = (ruleId) => {
    onChange(safeScores, safeRules.filter((r) => r._id !== ruleId));
  };

  const optionsForCategory = (categoryName) => {
    const category = safeCategories.find((c) => c.name === categoryName);
    return category?.options || [];
  };

  return (
    <Box>
      <Typography variant="overline" display="block">Scores</Typography>
      {safeScores.map((score) => {
        const rulesForScore = safeRules.filter((r) => r.scoreDefinitionId === score._id);
        return (
          <Paper key={score._id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid size={1}>
                <ScoreColorPicker
                  color={score.color}
                  autoColor={getScoreColor(score._id, safeScores)}
                  disabled={disabled}
                  onChange={(nextColor) => updateScore(score._id, { color: nextColor })}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Score name"
                  disabled={disabled}
                  value={score.name}
                  onChange={(e) => updateScore(score._id, { name: e.target.value })}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Description"
                  disabled={disabled}
                  value={score.description || ''}
                  onChange={(e) => updateScore(score._id, { description: e.target.value })}
                />
              </Grid>
              <Grid size={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      disabled={disabled}
                      checked={score.showOnJurorCard !== false}
                      onChange={(e) => updateScore(score._id, { showOnJurorCard: e.target.checked })}
                    />
                  }
                  label="Show on juror card"
                />
              </Grid>
              <Grid size={1}>
                <IconButton size="small" disabled={disabled} onClick={() => removeScore(score._id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>

            <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Scoring rules — each rule maps a category + value to a delta on this score. Pick
                "No answer / not set" as the value to configure what a juror with no entry for that
                category contributes.
              </Typography>
              {safeCategories.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Add at least one category before adding rules.
                </Typography>
              ) : (
                <React.Fragment>
                  {rulesForScore.length > 0 && (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Delta</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rulesForScore.map((rule) => (
                          <TableRow key={rule._id}>
                            <TableCell>
                              <TextField
                                select
                                size="small"
                                fullWidth
                                disabled={disabled}
                                value={rule.categoryName || ''}
                                onChange={(e) => updateRule(rule._id, { categoryName: e.target.value, optionValue: null })}
                              >
                                {safeCategories.map((c) => (
                                  <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <TextField
                                select
                                size="small"
                                fullWidth
                                disabled={disabled}
                                value={rule.optionValue === null ? NO_ANSWER_SENTINEL : (rule.optionValue ?? '')}
                                onChange={(e) => updateRule(rule._id, {
                                  optionValue: e.target.value === NO_ANSWER_SENTINEL ? null : e.target.value,
                                })}
                              >
                                <MenuItem value={NO_ANSWER_SENTINEL}><em>No answer / not set</em></MenuItem>
                                {optionsForCategory(rule.categoryName).map((option) => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                disabled={disabled}
                                value={rule.delta}
                                onChange={(e) => updateRule(rule._id, { delta: parseFloat(e.target.value) || 0 })}
                                sx={{ width: 90 }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" disabled={disabled} onClick={() => removeRule(rule._id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  <Button
                    size="small"
                    startIcon={<Add />}
                    disabled={disabled}
                    onClick={() => addRule(score._id)}
                    sx={{ mt: rulesForScore.length > 0 ? 1 : 0 }}
                  >
                    Add rule
                  </Button>
                </React.Fragment>
              )}
            </Box>
          </Paper>
        );
      })}
      <Button size="small" startIcon={<Add />} disabled={disabled} onClick={addScore}>Add score</Button>
    </Box>
  );
}
